import React, { useState, useEffect } from "react";
import { Checkbox } from "antd";
import CustomListItem from "./CustomListItem";

/**
 * Custom List Selector Component
 *
 * @param {Array} data - Array of items to display
 * @param {string} selectionType - Type of selection: 'radio', 'checkbox', or 'none'
 * @param {Array|string|number} value - Selected value(s) - array for checkbox, single value for radio
 * @param {function} onChange - Callback when selection changes
 * @param {ReactNode} customItem - Custom item render element (optional)
 * @param {boolean} showSelectAll - Show "Select All" checkbox (only for checkbox mode)
 * @param {string} selectAllText - Text for "Select All" checkbox
 * @param {ReactNode} customAvatar - Custom avatar element
 * @param {ReactNode} customTitle - Custom title element
 * @param {ReactNode} customSubtitle - Custom subtitle element
 * @param {ReactNode} customExtra - Custom extra content
 * @param {string} className - Additional CSS classes
 */
const CustomListSelector = ({
  data = [],
  selectionType = "none", // 'radio', 'checkbox', 'none'
  value = null,
  onChange,
  customItem,
  showSelectAll = true,
  selectAllText = "Select All",
  customAvatar,
  customTitle,
  customSubtitle,
  customExtra,
  avatarSize = 40,
  className = "",
}) => {
  const [selectedItems, setSelectedItems] = useState(selectionType === "checkbox" ? [] : null);

  // Sync internal state with external value prop
  useEffect(() => {
    if (value !== undefined && value !== null) {
      setSelectedItems(value);
    }
  }, [value]);

  // Handle checkbox selection
  const handleCheckboxSelect = itemId => {
    let newSelection;
    if (selectedItems.includes(itemId)) {
      newSelection = selectedItems.filter(id => id !== itemId);
    } else {
      newSelection = [...selectedItems, itemId];
    }
    setSelectedItems(newSelection);
    if (onChange) {
      onChange(newSelection);
    }
  };

  // Handle radio selection
  const handleRadioSelect = itemId => {
    setSelectedItems(itemId);
    if (onChange) {
      onChange(itemId);
    }
  };

  // Handle Select All
  const handleSelectAll = checked => {
    if (checked) {
      const allIds = data.map(item => item.id || item.key);
      setSelectedItems(allIds);
      if (onChange) {
        onChange(allIds);
      }
    } else {
      setSelectedItems([]);
      if (onChange) {
        onChange([]);
      }
    }
  };

  // Check if all items are selected (for Select All checkbox)
  const isAllSelected =
    selectionType === "checkbox" && data.length > 0 && selectedItems.length === data.length;

  // Check if some items are selected (for indeterminate state)
  const isSomeSelected =
    selectionType === "checkbox" && selectedItems.length > 0 && selectedItems.length < data.length;

  return (
    <div className={`w-full ${className}`}>
      {/* Select All Checkbox */}
      {selectionType === "checkbox" && showSelectAll && data.length > 0 && (
        <div className="mb-3 px-3">
          <Checkbox
            checked={isAllSelected}
            indeterminate={isSomeSelected}
            onChange={e => handleSelectAll(e.target.checked)}
          >
            <span className="text-sm font-normal text-black">{selectAllText}</span>
          </Checkbox>
        </div>
      )}

      {/* List Items */}
      <div className="space-y-2">
        {data.map(item => {
          const itemId = item.id || item.key;
          const isSelected =
            selectionType === "checkbox"
              ? selectedItems.includes(itemId)
              : selectionType === "radio"
              ? selectedItems === itemId
              : false;

          const handleSelect = id => {
            if (selectionType === "checkbox") {
              handleCheckboxSelect(id);
            } else if (selectionType === "radio") {
              handleRadioSelect(id);
            }
          };

          // If custom item is provided, use it
          if (customItem) {
            return React.cloneElement(customItem, {
              key: itemId,
              item,
              selected: isSelected,
              onSelect: handleSelect,
            });
          }

          // Otherwise use CustomListItem
          return (
            <CustomListItem
              key={itemId}
              item={item}
              selected={isSelected}
              onSelect={handleSelect}
              selectionType={selectionType}
              customAvatar={customAvatar}
              customTitle={customTitle}
              customSubtitle={customSubtitle}
              customExtra={customExtra}
              avatarSize={avatarSize}
            />
          );
        })}
      </div>

      {/* Empty State */}
      {data.length === 0 && (
        <div className="text-center p-8 text-gray-400">No items to display</div>
      )}
    </div>
  );
};

export default CustomListSelector;
